import { useEffect, useState } from 'react';

// 30개 대표 한국 성씨 풀 — "함께 보고 있어요" 아바타용
export const SURNAMES = [
  '김', '이', '박', '최', '정', '강', '조', '윤', '장', '임',
  '한', '오', '서', '신', '권', '황', '안', '송', '류', '전',
  '홍', '고', '문', '양', '손', '배', '백', '허', '유', '남',
];

export interface RecentApplicant {
  name: string;       // 서버에서 이미 마스킹됨 (예: "설○○")
  createdAt: string;  // ISO 8601 UTC
}

export interface BriefingStats {
  total: number | null;
  last24h: number | null;
  recentApplicants: RecentApplicant[];
  loading: boolean;
  error: boolean;
  tick: number;       // 30초마다 증가 — 상대시간/D-day 재계산 트리거
}

const ENDPOINT = 'https://peetsunbae.com/waiting/winter26-briefing-stats';

// 페이지 진입 시 1회만 호출. polling 없음 (서버 팀 지침).
// tick 값은 30초마다 증가해 컴포넌트가 상대시간을 재계산하게 한다.
export function useCentumBriefingStats(location: string = 'centum'): BriefingStats {
  const [state, setState] = useState<Omit<BriefingStats, 'tick'>>({
    total: null,
    last24h: null,
    recentApplicants: [],
    loading: true,
    error: false,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${ENDPOINT}?location=${encodeURIComponent(location)}`, {
      method: 'GET',
      signal: controller.signal,
    })
      .then(res => res.json())
      .then((result: any) => {
        if (result?.message === 'success') {
          setState({
            total: typeof result.total === 'number' ? result.total : null,
            last24h: typeof result.last24h === 'number' ? result.last24h : null,
            recentApplicants: Array.isArray(result.recentApplicants) ? result.recentApplicants : [],
            loading: false,
            error: false,
          });
        } else {
          setState(s => ({ ...s, loading: false, error: true }));
        }
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') return;
        setState(s => ({ ...s, loading: false, error: true }));
      });

    return () => controller.abort();
  }, [location]);

  // 30초 tick — 상대시간 / D-day 자동 갱신용 (서버 재호출 아님)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  return { ...state, tick };
}

// 배열에서 N개를 중복 없이 무작위 추출 (Fisher–Yates partial shuffle)
export function pickRandom<T>(arr: T[], n: number): T[] {
  const a = arr.slice();
  const len = a.length;
  const take = Math.min(n, len);
  for (let i = 0; i < take; i++) {
    const j = i + Math.floor(Math.random() * (len - i));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, take);
}

// "2026-06-06 00:00 KST" 같은 이벤트 ISO 를 받아 D-day 문자열 반환
// > 0  → "D-N"
// = 0  → "D-DAY"
// < 0  → "종료"
export function formatDday(eventISO: string, now: Date = new Date()): string {
  const dayMs = 86_400_000;
  const kstOffsetMs = 9 * 60 * 60 * 1000;
  const toKstMidnight = (d: Date) =>
    Math.floor((d.getTime() + kstOffsetMs) / dayMs) * dayMs;

  const event = new Date(eventISO);
  const diff = Math.round((toKstMidnight(event) - toKstMidnight(now)) / dayMs);

  if (diff > 0) return `D-${diff}`;
  if (diff === 0) return 'D-DAY';
  return '종료';
}

// ISO → "방금 전" / "N분 전" / "N시간 전" / "어제" / "N일 전"
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime();
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return '방금 전';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day === 1) return '어제';
  return `${day}일 전`;
}

export const CENTUM_EVENT_ISO = '2026-06-06T00:00:00+09:00';

// 센텀점 설명회 예약 마감 여부 — 다시 열려면 false 로 변경
// (전 지점 마감 플래그는 briefingBranchConfig.ts 의 ALL_BRIEFING_CLOSED 참고)
export const CENTUM_RESERVATION_CLOSED = true;
